import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';

import { get, post, put } from 'api/requests';

import { updateCalendar, setCalendar } from './calendarSlice';
import { updateDistribution, deleteDistributionStoreItem } from './distributionSlice';
import { loading as commonLoading } from 'store/common';

import { getMinutesFromTime, getTimeFromMinutes } from 'helpers';

import { IReducer, AppThunk } from 'store/types';
import {
  IDraggable,
  IDragSource,
  IDragData,
  ISplitPlacements,
  ITimeItem,
} from 'pages/Timesheet/TimesheetApartments/types';
import { ITaskData, IParamsReq } from 'types';

const initialState: IDraggable = {
  loading: false,
  draggable: false,
  current: {} as ITaskData,
  splitPlacements: {} as ISplitPlacements,
  showSplitModal: false,
  data: {
    dropId: '',
    dragId: '',
    dragParentId: '',
    dropParentId: '',
    dragSource: '' as IDragSource,
    dropSource: '' as IDragSource,
    dragTaskMinutes: 0,
    placementType: [],
  },
};

const splitPlacements: CaseReducer<IDraggable, PayloadAction<ISplitPlacements>> = (
  state,
  action,
) => {
  state.splitPlacements = action.payload;
};

const changeSplitModal: CaseReducer<IDraggable> = state => {
  state.showSplitModal = !state.showSplitModal;
};

const reset: CaseReducer<IDraggable> = () => initialState;

const draggable: CaseReducer<IDraggable, PayloadAction<boolean>> = (state, action) => {
  state.draggable = action.payload;
};

const data: CaseReducer<IDraggable, PayloadAction<IDragData>> = (state, action) => {
  state.data = action.payload;
};

const loading: IReducer<IDraggable, boolean> = commonLoading;

const current: CaseReducer<IDraggable, PayloadAction<ITaskData>> = (state, action) => {
  state.current = action.payload;
};

export const draggableSlice = createSlice({
  name: 'OVP/drag',
  initialState,
  reducers: {
    draggable,
    current,
    loading,
    data,
    reset,
    changeSplitModal,
    splitPlacements,
  },
});

const {
  draggable: setDraggable,
  current: setDragCurrent,
  reset: resetState,
  data: setDragData,
  loading: setLoading,
  changeSplitModal: changeShowSplitModal,
  splitPlacements: setSplitPlacements,
} = draggableSlice.actions;

export const setData =
  (props: Partial<IDragData>): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { loadingOvp } = timesheet;

    dispatch(setDragData({ ...loadingOvp.drag.data, ...props }));
  };

export const setCurrent =
  (props: Partial<ITaskData>): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { loadingOvp } = timesheet;

    dispatch(setDragCurrent({ ...loadingOvp.drag.current, ...props }));
  };

export const createTask =
  (search: IParamsReq): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { loadingOvp } = timesheet;
    const { current, data: dragData } = loadingOvp.drag;
    const { startTime, endTime, id } = current;

    const { employees } = search;

    const dataObj = {
      startTime,
      endTime,
      workDay: dragData.dropParentId,
      appointment: id,
    };

    dispatch(setLoading(true));

    const correctUrl = employees
      ? `/api/timesheet/scs/?employees=${employees}`
      : '/api/timesheet/scs/';

    post<
      {
        startTime: string;
        endTime: string;
        workDay: string;
        appointment: string;
      },
      ITimeItem[]
    >(correctUrl, dataObj)
      // TODO type узнать
      .then(({ data }) => {
        // const response = data;
        dispatch(resetState());
        dispatch(setCalendar(data));
        dispatch(deleteDistributionStoreItem(dataObj));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const moveTask =
  (search: IParamsReq): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { loadingOvp } = timesheet;
    const { current, data: dragData } = loadingOvp.drag;
    const { project } = search;

    const data = {
      startTime: current.startTime,
      endTime: current.endTime,
      workDay: dragData.dropParentId,
      appointment: current.id,
    };

    dispatch(setLoading(true));

    put(`/api/timesheet/schedule/move/scs/${current.id}/`, data)
      .then(() => {
        dispatch(resetState());
        dispatch(updateCalendar(search));
        dispatch(updateDistribution(Number(project)));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const splitTask =
  (search: IParamsReq, selected?: (string | number | boolean)[], duration?: number): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { loadingOvp } = timesheet;
    const { current, data: dragData } = loadingOvp.drag;
    const { project } = search;

    const data = {
      startTime: current.startTime,
      endTime: duration
        ? getTimeFromMinutes(getMinutesFromTime(current.startTime) + duration)
        : current.endTime,
      workDay: dragData.dropParentId,
      appointment: current.id,
    };

    const placements = selected?.map(placement => {
      return { id: placement };
    });

    dispatch(setLoading(true));
    post(
      `/api/timesheet/appointments/${current.id}/split/`,
      placements ? { ...data, placements } : data,
    )
      .then(() => {
        dispatch(resetState());
        dispatch(updateCalendar(search));
        dispatch(updateDistribution(Number(project)));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const fetchSplitPlacements = (): AppThunk => (dispatch, getState) => {
  const { timesheet } = getState();
  const { loadingOvp } = timesheet;

  dispatch(setLoading(true));

  get<ISplitPlacements>(`/api/timesheet/appointments/${loadingOvp.drag.current.id}/placements/`)
    .then(({ data }) => {
      dispatch(setSplitPlacements(data));
    })
    .finally(() => dispatch(setLoading(false)));
};

export { setDraggable, resetState, changeShowSplitModal };
